import React from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import getSymbolFromCurrency from "currency-symbol-map";

import {
  Text,
  Title,
  Group,
  Paper,
  Box,
  Checkbox,
  Divider,
} from "@mantine/core";

import useLangUser from "../../../../utils/LangUser";
import TiptapReadonly from "../../../tiptap/components/TiptapReadonly";
import CouponButton from "./CouponButton";
import TaxAndDiscount from "./TaxAndDiscount";

export default function BundlesList({
  data,
  bundlesType,
  notLoggedIn,
  shouldShowPaymentBox,
  selectedBundle,
  setSelectedBundle,
  onApplyCode,
  tax_amount,
  discount_type,
  discount_value
}) {
  const { t } = useTranslation();
  const { language } = useLangUser();

  const discount = typeof discount_value == "number" ? discount_value : 0;

  if (!Array.isArray(data)) {
    return null;
  }

  const getBundlePaymentDetails = (bundle) => {
    const currency = getSymbolFromCurrency(bundle.currency);
    const amount = ensure2Dec(bundle.default_payment_amount);
    const frequency = bundle.default_payment_frequency != "once" ? ` / ${bundle.default_payment_frequency.replace("per ", "")}` : "";
    if (bundle.default_payment_frequency == "once") {
      return `One time payment of ${currency}${amount}`;
    }
    if (bundle.subscription_type == "ongoing") {
      if (bundle.default_payment_frequency == "per month") {
        return `Monthly subscription of ${currency}${amount}${frequency}`;
      } else {
        return `Annual subscription of ${currency}${amount}${frequency}`;
      }
    }
    if (bundle.number_of_times && bundle.number_of_times > 0) {
      return `${bundle.number_of_times} ${bundle.number_of_times == 1 ? "payment" : "payments"} of ${currency}${amount}`;
    }
    if (bundle.until_date) {
      return `${currency}${amount} ${frequency} until ${DateTime.fromISO(bundle.until_date)
        .setLocale(language)
        .toFormat("MMM d, y")}`;
    }
    return `${currency}${amount}`;
  };

  const ensure2Dec = (value) => {
    value = value ? parseFloat(value) : 0;
    return value.toFixed(2).replace(".00", "");
  };

  return (
    <>
      {data.map((bundle, key) => {
        const isChecked = selectedBundle && selectedBundle.id == bundle.id;
        const price = Number(bundle.default_payment_amount);
        const hasDescription = Array.isArray(bundle.description?.content) && bundle.description.content.filter(item => !(item.type === "paragraph" && !('content' in item))).length > 0;

        const isAvailablePayment =
          typeof bundle.max_bundle_purchasers === "number" && bundle.max_bundle_purchasers > 0
          ? (bundle.max_bundle_purchasers - (bundle.bundle_paid_count || 0)) > 0
          : true;

        if ((bundlesType === "bundle" && !isChecked) || !bundle.show_in_checkout && !isChecked) {
          return null;
        }
        return (
          <Paper
            key={key}
            withBorder
            shadow="xs"
            p={0}
            mt={30}
            radius="md"
            onClick={() => {
              if (bundlesType !== "bundle" && !!isAvailablePayment && !notLoggedIn) {
                setSelectedBundle(isChecked ? null : bundle)
              }
            }}
          >
            <Box pt={25} pb={8} pl={35} pr={35}>
              <Group sx={{ alignItems: "center" }} mb={20}>
                {bundlesType == "regular" && !!isAvailablePayment && !notLoggedIn && (
                  <Checkbox size="lg" checked={isChecked} />
                )}
                <Title
                  sx={(theme) => ({ fontSize: 24, fontWeight: 600 })}
                  order={2}
                >
                  {bundle.name}
                </Title>
              </Group>

              {!shouldShowPaymentBox && !!isAvailablePayment && !notLoggedIn && (
                <CouponButton onApplyCode={onApplyCode} />
              )}

              {!bundle.is_flexible && (
                <Text mt={5} mb={10} size="lg" color="dimmed">
                  {`${getBundlePaymentDetails(bundle)}`}
                </Text>
              )}

              {hasDescription && (
                <>
                  <TiptapReadonly content={bundle.description} />
                  <Divider
                    sx={(theme) => ({ borderTopColor: theme.colors.gray[2] })}
                    my="sm"
                  />
                </>
              )}

              <TaxAndDiscount
                currency={getSymbolFromCurrency(bundle.currency)}
                tax_amount={isChecked ? tax_amount : null}
                discount_type={isChecked ? discount_type : null}
                discount_value={isChecked ? discount_value : null}
              />

              {isAvailablePayment === false ? (
                <Group position="apart" mb={10}>
                  <Text size="lg">{"This option is no longer available."}</Text>
                </Group>
              ) : (
                <Group position="apart" mb={10}>
                  {bundle.trial_period ? (
                    <Text size="lg">{`Total due in ${bundle.trial_period} days`}</Text>
                  ) : (
                    <Text size="lg">{t("users.spaceReference.total")}</Text>
                  )}
                  <Text size="lg" sx={(theme) => ({ fontWeight: 600 })}>
                    {`${getSymbolFromCurrency(bundle.currency)}
                      ${ensure2Dec(
                        price + (isChecked && tax_amount ? tax_amount : 0) - (isChecked ? (discount_type === "percent" ? (price * discount / 100) : discount) : 0)
                      )}
                    `}
                  </Text>
                </Group>
              )}

            </Box>
          </Paper>
        );
      })}
    </>
  );
}
